import React, { useState, useEffect, useRef } from 'react';
import { Bell, Clock, Volume2, VolumeX, Upload, Play, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PrayerTimesSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const audioRef = useRef(null);
  const warningAudioRef = useRef(null);

  // Settings state
  const [settings, setSettings] = useState({
    // Notification settings
    notifyBefore5Min: true,
    notifyOnAdhan: true,
    warningMinutes: 5,
    
    // Sound settings
    soundEnabled: true,
    playAdhanSound: true,
    adhanSoundUrl: '',
    warningSoundUrl: '',
    
    // Volume levels (0-100)
    adhanVolume: 80,
    warningVolume: 70,
    
    // Default city
    defaultCity: 'İstanbul',
    defaultCountry: 'Türkiye'
  });

  // File upload states
  const [adhanFile, setAdhanFile] = useState(null);
  const [warningFile, setWarningFile] = useState(null);
  const [uploadingAdhan, setUploadingAdhan] = useState(false);
  const [uploadingWarning, setUploadingWarning] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/prayer-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error loading prayer settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/prayer-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast({
          title: "Başarılı",
          description: "Namaz vakti ayarları kaydedildi",
        });
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Error saving prayer settings:', error);
      toast({
        title: "Hata",
        description: "Ayarlar kaydedilemedi",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const uploadSound = async (file, type) => {
    if (!file) return;
    
    const setUploading = type === 'adhan' ? setUploadingAdhan : setUploadingWarning;
    setUploading(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/prayer-sounds/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (type === 'adhan') {
          setSettings(prev => ({ ...prev, adhanSoundUrl: data.url }));
          setAdhanFile(null);
        } else {
          setSettings(prev => ({ ...prev, warningSoundUrl: data.url }));
          setWarningFile(null);
        }
        
        toast({
          title: "Başarılı",
          description: `${type === 'adhan' ? 'Ezan' : 'Uyarı'} sesi yüklendi`,
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading sound:', error);
      toast({
        title: "Hata",
        description: "Ses dosyası yüklenemedi",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const playTestSound = (type) => {
    const audioElement = type === 'adhan' ? audioRef.current : warningAudioRef.current;
    const url = type === 'adhan' ? settings.adhanSoundUrl : settings.warningSoundUrl;
    const volume = type === 'adhan' ? settings.adhanVolume : settings.warningVolume;
    
    if (audioElement && url) {
      audioElement.src = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
      audioElement.volume = volume / 100;
      audioElement.play().catch(err => console.log('Audio play error:', err));
      
      // Stop after 5 seconds
      setTimeout(() => {
        audioElement.pause();
        audioElement.currentTime = 0;
      }, 5000);
    } else {
      toast({
        title: "Uyarı",
        description: "Önce ses dosyası yükleyin",
        variant: "destructive"
      });
    }
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#D5C4A1]/30 border-t-[#8B7355] rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden audio elements */}
      <audio ref={audioRef} preload="none" />
      <audio ref={warningAudioRef} preload="none" />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#5C4033]">Namaz Vakitleri Ayarları</h2>
          <p className="text-[#8B7355]">Bildirim, ses ve varsayılan ayarları yönetin</p>
        </div>
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className="bg-[#8B7355] hover:bg-[#6B5744]"
        >
          {saving ? (
            <>
              <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"></div>
              Kaydediliyor...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Kaydet
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notification Settings */}
        <Card className="border-[#E8DCC8]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#5C4033]">
              <Bell className="w-5 h-5 text-[#8B7355]" />
              Bildirim Ayarları
            </CardTitle>
            <CardDescription>Namaz vakti bildirimlerini yapılandırın</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 5 Min Warning */}
            <div className="flex items-center justify-between p-3 bg-[#F9F6F1] rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#8B7355]" />
                <div>
                  <p className="font-medium text-[#5C4033]">5 Dakika Önceden Uyarı</p>
                  <p className="text-sm text-[#8B7355]">Ezan vaktinden önce uyarı bildirimi</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('notifyBefore5Min')}
                className={`w-12 h-6 rounded-full transition-colors ${settings.notifyBefore5Min ? 'bg-[#8B7355]' : 'bg-gray-300'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-0.5 ${settings.notifyBefore5Min ? 'translate-x-6' : ''}`} />
              </button>
            </div>

            {/* Adhan Notification */}
            <div className="flex items-center justify-between p-3 bg-[#F9F6F1] rounded-lg">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-[#8B7355]" />
                <div>
                  <p className="font-medium text-[#5C4033]">Ezan Vakti Bildirimi</p>
                  <p className="text-sm text-[#8B7355]">Ezan vakti geldiğinde bildirim</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('notifyOnAdhan')}
                className={`w-12 h-6 rounded-full transition-colors ${settings.notifyOnAdhan ? 'bg-[#8B7355]' : 'bg-gray-300'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-0.5 ${settings.notifyOnAdhan ? 'translate-x-6' : ''}`} />
              </button>
            </div>

            {/* Warning Minutes */}
            <div className="p-3 bg-[#F9F6F1] rounded-lg">
              <label className="block mb-2">
                <span className="font-medium text-[#5C4033]">Uyarı Süresi (Dakika)</span>
                <span className="text-sm text-[#8B7355] ml-2">Ezandan kaç dakika önce uyarı verilsin?</span>
              </label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings.warningMinutes}
                onChange={(e) => setSettings(prev => ({ ...prev, warningMinutes: parseInt(e.target.value) || 5 }))}
                className="w-32 border-[#E8DCC8]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sound Settings */}
        <Card className="border-[#E8DCC8]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#5C4033]">
              <Volume2 className="w-5 h-5 text-[#8B7355]" />
              Ses Ayarları
            </CardTitle>
            <CardDescription>Ezan ve uyarı seslerini yapılandırın</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sound Enabled */}
            <div className="flex items-center justify-between p-3 bg-[#F9F6F1] rounded-lg">
              <div className="flex items-center gap-3">
                {settings.soundEnabled ? <Volume2 className="w-5 h-5 text-[#8B7355]" /> : <VolumeX className="w-5 h-5 text-gray-400" />}
                <div>
                  <p className="font-medium text-[#5C4033]">Ses Açık</p>
                  <p className="text-sm text-[#8B7355]">Tüm sesleri aç/kapat</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('soundEnabled')}
                className={`w-12 h-6 rounded-full transition-colors ${settings.soundEnabled ? 'bg-[#8B7355]' : 'bg-gray-300'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-0.5 ${settings.soundEnabled ? 'translate-x-6' : ''}`} />
              </button>
            </div>

            {/* Play Adhan Sound */}
            <div className="flex items-center justify-between p-3 bg-[#F9F6F1] rounded-lg">
              <div className="flex items-center gap-3">
                <Volume2 className="w-5 h-5 text-[#8B7355]" />
                <div>
                  <p className="font-medium text-[#5C4033]">Ezan Sesi Çal</p>
                  <p className="text-sm text-[#8B7355]">Ezan vakti gelince ses çal</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('playAdhanSound')}
                className={`w-12 h-6 rounded-full transition-colors ${settings.playAdhanSound ? 'bg-[#8B7355]' : 'bg-gray-300'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-0.5 ${settings.playAdhanSound ? 'translate-x-6' : ''}`} />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Adhan Sound Upload */}
        <Card className="border-[#E8DCC8]">
          <CardHeader>
            <CardTitle className="text-[#5C4033]">🕌 Ezan Sesi</CardTitle>
            <CardDescription>Ezan vakti geldiğinde çalınacak ses</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Sound */}
            {settings.adhanSoundUrl && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">Ezan sesi yüklü</p>
                  <p className="text-xs text-green-600 truncate">{settings.adhanSoundUrl}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => playTestSound('adhan')}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  <Play className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
            )}

            {/* Upload */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#5C4033]">
                Yeni Ezan Sesi Yükle
              </label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setAdhanFile(e.target.files[0])}
                  className="flex-1 border-[#E8DCC8]"
                />
                <Button
                  onClick={() => uploadSound(adhanFile, 'adhan')}
                  disabled={!adhanFile || uploadingAdhan}
                  className="bg-[#8B7355] hover:bg-[#6B5744]"
                >
                  {uploadingAdhan ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></div>
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Volume */}
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Ezan Ses Seviyesi: {settings.adhanVolume}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.adhanVolume}
                onChange={(e) => setSettings(prev => ({ ...prev, adhanVolume: parseInt(e.target.value) }))}
                className="w-full h-2 bg-[#E8DCC8] rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </CardContent>
        </Card>

        {/* Warning Sound Upload */}
        <Card className="border-[#E8DCC8]">
          <CardHeader>
            <CardTitle className="text-[#5C4033]">⏰ 5 Dakika Uyarı Sesi</CardTitle>
            <CardDescription>Ezandan 5 dakika önce çalınacak ses</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Sound */}
            {settings.warningSoundUrl && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Uyarı sesi yüklü</p>
                  <p className="text-xs text-blue-600 truncate">{settings.warningSoundUrl}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => playTestSound('warning')}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <Play className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
            )}

            {/* Upload */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#5C4033]">
                Yeni Uyarı Sesi Yükle
              </label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setWarningFile(e.target.files[0])}
                  className="flex-1 border-[#E8DCC8]"
                />
                <Button
                  onClick={() => uploadSound(warningFile, 'warning')}
                  disabled={!warningFile || uploadingWarning}
                  className="bg-[#8B7355] hover:bg-[#6B5744]"
                >
                  {uploadingWarning ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></div>
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Volume */}
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Uyarı Ses Seviyesi: {settings.warningVolume}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.warningVolume}
                onChange={(e) => setSettings(prev => ({ ...prev, warningVolume: parseInt(e.target.value) }))}
                className="w-full h-2 bg-[#E8DCC8] rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Default Location */}
      <Card className="border-[#E8DCC8]">
        <CardHeader>
          <CardTitle className="text-[#5C4033]">📍 Varsayılan Konum</CardTitle>
          <CardDescription>Sayfa açıldığında gösterilecek varsayılan şehir</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">Varsayılan Ülke</label>
              <select
                value={settings.defaultCountry}
                onChange={(e) => setSettings(prev => ({ ...prev, defaultCountry: e.target.value }))}
                className="w-full px-3 py-2 border border-[#E8DCC8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B7355]"
              >
                <option value="Türkiye">Türkiye</option>
                <option value="İsveç">İsveç</option>
                <option value="Almanya">Almanya</option>
                <option value="Fransa">Fransa</option>
                <option value="Hollanda">Hollanda</option>
                <option value="Belçika">Belçika</option>
                <option value="İngiltere">İngiltere</option>
                <option value="Avusturya">Avusturya</option>
                <option value="İsviçre">İsviçre</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">Varsayılan Şehir</label>
              <Input
                value={settings.defaultCity}
                onChange={(e) => setSettings(prev => ({ ...prev, defaultCity: e.target.value }))}
                className="border-[#E8DCC8]"
                placeholder="Örn: İstanbul"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-800">Bilgi</h4>
            <ul className="text-sm text-blue-700 mt-1 space-y-1">
              <li>• Ses dosyaları MP3 formatında olmalıdır</li>
              <li>• Maksimum dosya boyutu 10MB'dır</li>
              <li>• Değişiklikler kaydedildikten sonra kullanıcıların sayfayı yenilemesi gerekebilir</li>
              <li>• Bildirimler için kullanıcının tarayıcı izni vermesi gerekir</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrayerTimesSettings;
